import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { classNames } from "@via/utils/helpers.tsx";
import { type TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { type JSX, Fragment } from "react";
import { Spinner } from "@via/components/loading/loading";

/** * This component is a standard pattern we will use as a "context menu" for rows in data tables.
     Click the vertical ellipsis icon, and you'll see options to manage data in the table from dropdown ***/
export default function TableContextMenu({
  options,
  onSelect,
  className,
}: {
  options: TableContextMenuInterface[];
  onSelect: (option: TableContextMenuInterface) => void;
  className?: string;
}): JSX.Element {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          className={classNames(
            "flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none",
            className ?? ""
          )}>
          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <Menu.Items className="absolute right-0 z-10 mt-2 w-43 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {options.map((item, index) => (
              <Menu.Item key={item.id ?? `${item.name}-${index}`}>
                {({ active }) => (
                  <button
                    {...(item?.id ? { "data-testid": item.id } : {})}
                    disabled={item?.disabled || item?.loading}
                    title={item?.title}
                    onClick={() => {
                      onSelect(item);
                    }}
                    className={classNames(
                      active ? "bg-gray-50" : "",
                      item?.disabled || item?.loading
                        ? "opacity-50 cursor-not-allowed"
                        : "",
                      "flex w-full items-center gap-2 px-3 py-1 text-left text-sm leading-6 text-gray-900"
                    )}>
                    {item.name}
                    {item.loading && (
                      <div>
                        <Spinner loading />
                      </div>
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
